import { Pipe, PipeTransform, inject, LOCALE_ID } from '@angular/core';
import { formatDate } from '@angular/common';
import dayjs from 'dayjs/esm';
import utc from 'dayjs/esm/plugin/utc';
import timezone from 'dayjs/esm/plugin/timezone';
import { PracticeTimezoneService } from '../services/practice-timezone.service';

// Self-contained init: this pipe must work without relying on any Material date
// adapter having loaded these plugins. Safe to call repeatedly (dayjs no-ops).
dayjs.extend(utc);
dayjs.extend(timezone);

// Angular DatePipe format tokens (NOT dayjs tokens) — so migrating a site is a
// near-mechanical `| date:'X'` -> `| practiceDateTime:'X'` keeping the format.
const DEFAULT_FORMAT = 'yyyy-MM-dd HH:mm';
const HINT_TIME_FORMAT = 'HH:mm';

export interface PracticeDateTimeOptions {
  /**
   * Force the zone label even when the viewer is in the practice zone.
   * Default false — in-zone viewers see just the time; the label appears
   * automatically when the viewer is out of zone.
   */
  alwaysShowZone?: boolean;
  /** Show the "(… your time)" hint when the viewer is out of zone. Default true. */
  showDeviceHint?: boolean;
  /**
   * How to interpret a **zone-less** input value (one with no `Z` / offset):
   *  - `'utc'` (default): it's a UTC instant — correct for the `*Utc` audit
   *    fields this pipe was built for.
   *  - `'practice'`: it's already **practice wall-clock** — correct for backend
   *    appointment `start`/`end` (serialized as zone-less local time). The wall
   *    numbers are kept as-is; the device hint is still derived from the instant.
   *
   * Inputs that already carry a zone, `Date`s, and epoch numbers are absolute
   * instants and are unaffected by this option.
   */
  sourceZone?: 'utc' | 'practice';
}

/**
 * practiceDateTime
 *
 * Renders a UTC instant in the **practice** timezone, always, with a zone label
 * — so every viewer sees one canonical time for a record. When the viewer's own
 * device zone resolves to a different offset for that instant, it appends their
 * local time as a secondary hint, e.g.:
 *
 *   `2026-06-09 14:30`                          (viewer in-zone — just the time)
 *   `2026-06-09 14:30 SAST (12:30 your time)`   (viewer out-of-zone — label + hint)
 *
 * Feed it the canonical `*Utc` instant (e.g. `createdDateUtc`), NOT the legacy
 * local field — the legacy field is zone-less local server time and would be
 * mis-converted. A `Z`-less ISO string is parsed defensively as UTC.
 *
 * Formatting uses Angular's `formatDate` with the practice zone's DST-correct
 * offset for that instant (computed via dayjs). The `format` argument therefore
 * takes ordinary Angular `DatePipe` tokens.
 *
 * Impure: the practice zone arrives asynchronously (settings load) and changes
 * on practice switch, so the pipe must recompute outside input-reference change.
 * The transform is cheap and memoized on its inputs to keep that affordable on
 * long lists.
 */
@Pipe({
  name: 'practiceDateTime',
  pure: false
})
export class PracticeDateTimePipe implements PipeTransform {
  private readonly tz = inject(PracticeTimezoneService);
  private readonly locale = inject(LOCALE_ID);

  // Single-entry memo: impure pipes are re-invoked every CD cycle, but the
  // inputs rarely change, so we recompute only when something actually differs.
  private lastKey: string | null = null;
  private lastResult = '';

  transform(
    value: Date | string | number | null | undefined,
    format: string = DEFAULT_FORMAT,
    options: PracticeDateTimeOptions = {}
  ): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    const alwaysShowZone = options.alwaysShowZone ?? false;
    const showDeviceHint = options.showDeviceHint ?? true;
    const sourceZone = options.sourceZone ?? 'utc';
    const practiceZone = this.tz.practiceTimezone();
    const deviceZone = this.tz.deviceTimezone;

    const key = `${stringifyValue(value)}|${format}|${practiceZone}|${deviceZone}|${alwaysShowZone}|${showDeviceHint}|${sourceZone}`;
    if (key === this.lastKey) {
      return this.lastResult;
    }

    const instant = toInstant(value, sourceZone, practiceZone);
    if (!instant) {
      this.lastKey = key;
      this.lastResult = '';
      return '';
    }

    // DST-correct offset for THIS instant in the practice zone, e.g. "+0200".
    const practiceOffset = dayjs(instant).tz(practiceZone).format('ZZ');
    let result = formatDate(instant, format, this.locale, practiceOffset);

    // In-zone viewers (the overwhelming majority) see just the time. The zone
    // label and the local-time hint appear only when the viewer is out of zone
    // — exactly when there is an ambiguity to resolve.
    const outOfZone = offsetDiffers(instant, practiceZone, deviceZone);

    if (outOfZone || alwaysShowZone) {
      result += ` ${zoneLabel(practiceZone, instant)}`;
    }

    if (outOfZone && showDeviceHint) {
      const deviceOffset = dayjs(instant).tz(deviceZone).format('ZZ');
      result += ` (${formatDate(instant, HINT_TIME_FORMAT, this.locale, deviceOffset)} your time)`;
    }

    this.lastKey = key;
    this.lastResult = result;
    return result;
  }
}

/** Stable string for the memo key regardless of Date vs string vs number input. */
function stringifyValue(value: Date | string | number): string {
  return value instanceof Date ? value.getTime().toString() : String(value);
}

/**
 * Parse the input to the absolute instant it represents.
 *
 * For a **zone-less** ISO string (e.g. `2026-06-09T14:30:00`):
 *  - `sourceZone === 'utc'` (default) treats it as UTC — defends against
 *    serialization that drops the trailing `Z` on `*Utc` audit fields.
 *  - `sourceZone === 'practice'` treats it as practice wall-clock — correct for
 *    backend appointment `start`/`end`, which are zone-less local time.
 *
 * Values that already carry a zone (`Z` or `±hh:mm`), Dates, and epoch numbers
 * are absolute instants and are parsed as-is regardless of `sourceZone`.
 * Returns null for unparseable input.
 */
function toInstant(
  value: Date | string | number,
  sourceZone: 'utc' | 'practice',
  practiceZone: string
): Date | null {
  // Trim incidental whitespace from string inputs (common in template bindings
  // / serialization) so they don't parse as invalid and render empty.
  const v = typeof value === 'string' ? value.trim() : value;
  let parsed: dayjs.Dayjs;
  if (typeof v === 'string' && isZonelessIso(v)) {
    parsed = sourceZone === 'practice' ? dayjs.tz(v, practiceZone) : dayjs.utc(v);
  } else {
    parsed = dayjs(v);
  }
  return parsed.isValid() ? parsed.toDate() : null;
}

const ZONELESS_ISO = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function isZonelessIso(value: string): boolean {
  return ZONELESS_ISO.test(value.trim());
}

/** True when the two zones resolve to a different UTC offset for this instant (DST-aware). */
function offsetDiffers(instant: Date, practiceZone: string, deviceZone: string): boolean {
  if (practiceZone === deviceZone) {
    return false;
  }
  return dayjs(instant).tz(practiceZone).utcOffset() !== dayjs(instant).tz(deviceZone).utcOffset();
}

/**
 * Short, human zone label for the instant (e.g. "SAST", or "GMT+2" where no
 * abbreviation exists). Uses en-ZA so SA zones read naturally; any zone falls
 * back to a GMT offset, which is unambiguous.
 */
function zoneLabel(zone: string, instant: Date): string {
  try {
    const parts = new Intl.DateTimeFormat('en-ZA', {
      timeZone: zone,
      timeZoneName: 'short'
    }).formatToParts(instant);
    const name = parts.find(p => p.type === 'timeZoneName')?.value;
    return name ?? zone;
  } catch {
    return zone;
  }
}
